import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiArrowUpRight, FiCheck, FiFileText, FiGlobe, FiLink, FiMessageSquare, FiPlay, FiShield, FiInbox, FiSend, FiZap } from 'react-icons/fi';
import { FaApple, FaAndroid, FaGooglePlay } from 'react-icons/fa';

export const products = [
  { id: 'legal-documents', label: 'Legal documents', mobileLabel: 'Legal docs', icon: FiFileText, eyebrow: 'THE PAPERWORK, SIMPLIFIED', title: 'Big ideas deserve\na solid foundation.', description: 'Create the legal documents your app needs, without starting from a blank page. Keep everything organized around your project.', features: ['Privacy policies & terms of service', 'Cookie policies, disclaimers & more', 'PDF exports and shareable document links'], video: 'oedoaW9z2-k', action: 'Create your documents' },
  { id: 'url-shortener', label: 'Smart links', icon: FiLink, eyebrow: 'ONE LINK. EVERY PLATFORM.', title: 'The right destination.\nEvery single time.', description: 'Turn a long URL into a smarter connection. Send visitors to the right destination for their device and understand where clicks come from.', features: ['Platform-specific redirects', 'Click and geographic analytics', 'Device and referral insights'], video: 'NSBXNag5Ulg', action: 'Create a smart link' },
  { id: 'customer-support', label: 'Customer support', mobileLabel: 'App tickets', icon: FiMessageSquare, eyebrow: 'IN-APP SUPPORT, CONNECTED', title: 'From inside your app.\nStraight to your inbox.', description: 'Let users create support tickets directly from your mobile app or website. Connect your support form to AppActs and manage every request in one workspace.', features: ['Ticket creation directly from your app', 'Connect your own support form with the API', 'Track priorities, replies and ticket status'], video: 'TZDdDymeQ04', action: 'Set up your support' },
  { id: 'internal-ads', label: 'Internal ads', icon: FiZap, eyebrow: 'MAKE YOUR NEXT APP KNOWN', title: 'Your next audience\nis already here.', description: 'Connect your products with in-app promotions. Manage placements and track clicks from the same place you manage your apps.', features: ['Cross-product promotion', 'Flexible ad placement management', 'Click tracking and API integration'], video: 'WSvJyzmPy80', action: 'Explore internal ads' },
  { id: 'landing-pages', label: 'Landing pages', icon: FiGlobe, eyebrow: 'GIVE YOUR IDEA A HOME', title: 'An introduction\nworth clicking.', description: 'Bring your app, links and information together on a responsive landing page. Give people a clear path from curious to connected.', features: ['Responsive pages for your products', 'App Store and Google Play download links', 'Connected links and project information'], video: 'w_YE2Uvd98I', action: 'Build your landing page' },
];

function SmartLinkPreview() {
  return <div className="home-link-demo">
    <div className="home-demo-icon"><FiLink aria-hidden="true" /></div>
    <span className="home-mini-label">ONE SMART LINK</span>
    <strong className="home-smart-link-source">appacts.com/s/orbit</strong>
    <svg className="home-link-connectors" viewBox="0 0 300 64" preserveAspectRatio="none" aria-hidden="true">
      <path d="M150 0V24 M50 59V32Q50 24 58 24H242Q250 24 250 32V59 M150 24V59" />
      <path d="M46 55L50 60L54 55 M146 55L150 60L154 55 M246 55L250 60L254 55" />
      <circle cx="150" cy="24" r="3" />
    </svg>
    <div className="home-platform-grid">
      {[[FaApple, 'iOS', 'App Store'], [FaAndroid, 'Android', 'Google Play'], [FiGlobe, 'Web', 'Your website']].map(([Icon, name, target]) =>
        <div key={name}><Icon aria-hidden="true" /><strong>{name}</strong><small>{target}</small></div>
      )}
    </div>
    <span className="home-visual-badge"><FiCheck aria-hidden="true" /> One link. Three destinations.</span>
  </div>;
}

function AppTicketPreview() {
  return <div className="home-ticket-demo">
    <div className="home-ticket-heading"><span>IN YOUR APP</span><span>IN YOUR WORKSPACE</span></div>
    <div className="home-ticket-flow">
      <div className="home-ticket-app">
        <div className="home-phone-notch" />
        <div className="home-ticket-app-heading"><FiMessageSquare aria-hidden="true" /><strong>Contact support</strong></div>
        <span className="home-ticket-field-label">Subject</span>
        <div className="home-ticket-field">Help with my account</div>
        <span className="home-ticket-field-label">Message</span>
        <div className="home-ticket-field home-ticket-message">I can't sync my latest changes. Can you help?</div>
        <span className="home-ticket-submit">Create ticket <FiSend aria-hidden="true" /></span>
      </div>
      <FiArrowRight className="home-ticket-flow-arrow" aria-hidden="true" />
      <div className="home-ticket-receipt">
        <span className="home-ticket-inbox-icon"><FiInbox aria-hidden="true" /></span>
        <span className="home-mini-label">APPACTS INBOX</span>
        <strong>New ticket #1042</strong>
        <p>Orbit · iOS app</p>
        <span className="home-status-dot">Open</span>
        <span className="home-ticket-received"><FiCheck aria-hidden="true" /> Request received</span>
      </div>
    </div>
    <p className="home-ticket-caption">Your app's support form. Your AppActs inbox.</p>
  </div>;
}

function LandingPreview() {
  return <div className="home-landing-demo">
    <div className="home-mini-site-nav"><strong>orbit.</strong><span>Your next adventure <FiArrowUpRight aria-hidden="true" /></span></div>
    <div className="home-mini-site-art"><span /><FiGlobe aria-hidden="true" /><span /></div>
    <span className="home-mini-label">A LITTLE SPACE FOR BIG IDEAS</span>
    <h3>Meet your next<br />favorite app.</h3>
    <div className="home-store-badges" aria-label="Example app download buttons">
      <span className="home-store-badge"><FaApple aria-hidden="true" /><span><small>Download on the</small><strong>App Store</strong></span></span>
      <span className="home-store-badge"><FaGooglePlay aria-hidden="true" /><span><small>GET IT ON</small><strong>Google Play</strong></span></span>
    </div>
    <div className="home-mini-site-footer"><span>Privacy</span><span>Support</span><span>All connected <FiCheck aria-hidden="true" /></span></div>
  </div>;
}

function ProductVisual({ product }) {
  return (
    <div className={`home-product-visual home-visual-${product.id}`} aria-label={`${product.label} example`}>
      <div className="home-visual-top"><span><i /> orbit.app</span><span>EXAMPLE</span></div>
      {product.id === 'legal-documents' && <div className="home-document-paper"><div className="home-document-icon"><FiShield /></div><span className="home-mini-label">ORBIT / LEGAL</span><h3>Privacy Policy</h3><p>A clear foundation for your next big idea.</p><div className="home-document-rule" /><strong>01. Information we collect</strong><div className="home-document-lines"><i /><i /><i /></div><strong>02. How we use your information</strong><div className="home-document-lines"><i /><i /></div><span className="home-visual-badge"><FiCheck /> Ready to share</span></div>}
      {product.id === 'url-shortener' && <SmartLinkPreview />}
      {product.id === 'customer-support' && <AppTicketPreview />}
      {product.id === 'internal-ads' && <div className="home-ad-demo"><span className="home-mini-label">YOUR APP. YOUR AUDIENCE.</span><div className="home-ad-phone"><div className="home-phone-notch" /><span>Discover something new</span><div className="home-ad-art"><FiZap /></div><h3>Meet Orbit.</h3><p>More room for your next idea.</p><span className="home-demo-cta">Explore the app <FiArrowUpRight /></span><small>App promotion · Example placement</small></div></div>}
      {product.id === 'landing-pages' && <LandingPreview />}
    </div>
  );
}

export default function ProductShowcase({ destination }) {
  const [active, setActive] = useState(0);
  const tabs = useRef([]);
  const tabList = useRef(null);

  const selectProduct = (index) => {
    setActive(index);
    if (!window.matchMedia('(max-width: 600px)').matches) return;
    const top = tabList.current.getBoundingClientRect().top;
    // Keep the selectors and the fixed-height preview together on small screens.
    if (top < 80 || top + tabList.current.offsetHeight + 316 > window.innerHeight) {
      tabList.current.scrollIntoView({
        block: 'start',
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    }
  };
  useEffect(() => {
    const readHash = () => {
      const index = products.findIndex(product => `#${product.id}` === window.location.hash);
      if (index >= 0) setActive(index);
    };
    readHash();
    window.addEventListener('hashchange', readHash);
    return () => window.removeEventListener('hashchange', readHash);
  }, []);

  const onKeyDown = (event, index) => {
    let next;
    if (event.key === 'ArrowRight') next = (index + 1) % products.length;
    if (event.key === 'ArrowLeft') next = (index + products.length - 1) % products.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = products.length - 1;
    if (next === undefined) return;
    event.preventDefault();
    selectProduct(next);
    tabs.current[next]?.focus();
  };
  const product = products[active];
  return (
    <section id="products" className="home-section home-products">
      <div className="home-container">
        <div className="home-section-heading home-reveal"><span className="home-eyebrow">01 / YOUR LAUNCH STACK</span><h2>Five tools. One less thing<br /> to worry about.</h2><p>From your first policy to your next product launch.<br className="home-desktop-break" /> Everything works better together.</p></div>
        <div className="home-product-tabs" ref={tabList} role="tablist" aria-label="Explore AppActs products">
          {products.map(({ id, label, mobileLabel, icon: Icon }, index) => <button type="button" key={id} id={id} role="tab" aria-label={label} aria-selected={index === active} aria-controls="home-product-panel" tabIndex={index === active ? 0 : -1} ref={node => { tabs.current[index] = node; }} onClick={() => selectProduct(index)} onKeyDown={event => onKeyDown(event, index)}><Icon aria-hidden="true" /><span className="home-tab-label">{label}</span><span className="home-tab-mobile-label" aria-hidden="true">{mobileLabel || label}</span></button>)}
        </div>
        <div id="home-product-panel" role="tabpanel" aria-labelledby={product.id} tabIndex={0} className="home-product-panel">
          <div className="home-product-transition" key={product.id}>
            <div className="home-product-copy"><span className="home-eyebrow">{product.eyebrow}</span><h3>{product.title}</h3><p>{product.description}</p><ul>{product.features.map(feature => <li key={feature}><FiCheck aria-hidden="true" />{feature}</li>)}</ul><Link className="home-text-link" to={destination}>{product.action}<FiArrowRight aria-hidden="true" /></Link><a className="home-video-link" href={`https://www.youtube.com/watch?v=${product.video}`} target="_blank" rel="noreferrer"><FiPlay aria-hidden="true" /> Watch walkthrough <span className="sr-only">(opens in a new tab)</span></a></div>
            <ProductVisual product={product} />
          </div>
        </div>
      </div>
    </section>
  );
}
